// NAME.azurecr.io, or NAME-HASH.azurecr.io for a registry with a domain name label scope.
// Registry names are alphanumeric only, so the dash always starts the hash.
const LOGIN_SERVER = String.raw`[a-z0-9]{5,50}(?:-[a-z0-9]+)?\.azurecr\.io`
const SEGMENT = String.raw`[a-z0-9]+(?:[._-][a-z0-9]+)*`
const LOGIN_SERVER_ONLY = new RegExp(`^${LOGIN_SERVER}$`)
const REPOSITORY = new RegExp(
  `^(?:${LOGIN_SERVER}/)?${SEGMENT}(?:/${SEGMENT})*$`
)
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const ACR_IDENTITY_NAME = "kodem-acr-reader"

// The registry's name, which az acr commands take: its login server without .azurecr.io and
// without a domain name label hash.
export const acrRegistryName = (loginServer: string) =>
  normalizeAcrLoginServer(loginServer)
    .replace(/\.azurecr\.io$/, "")
    .replace(/-.*$/, "")

export const normalizeAcrLoginServer = (value: string) => {
  const server = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
  return server && !server.includes(".") ? `${server}.azurecr.io` : server
}

export const isValidAcrLoginServer = (value: string) =>
  LOGIN_SERVER_ONLY.test(value)

export const isGuid = (value: string) => GUID.test(value.trim())

export const normalizeAcrRepository = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/@.*$/, "")
    .replace(/:[^/]*$/, "")
    .replace(/^\/+|\/+$/g, "")

// A repository, optionally with its login server, as rows store base images.
export const isValidAcrRepository = (value: string) =>
  REPOSITORY.test(value) && !LOGIN_SERVER_ONLY.test(value)

export interface AcrFederation {
  issuer: string
  subject: string
  audience: string
}

export const identityCreateCommand = () =>
  `az identity create --name ${ACR_IDENTITY_NAME} --resource-group RESOURCE_GROUP`

export const federatedCredentialCommand = ({
  issuer,
  subject,
  audience
}: AcrFederation) =>
  [
    "az identity federated-credential create --name kodem \\",
    `  --identity-name ${ACR_IDENTITY_NAME} --resource-group RESOURCE_GROUP \\`,
    `  --issuer ${issuer} \\`,
    `  --subject ${subject} \\`,
    `  --audiences ${audience}`
  ].join("\n")

// The login server the form has, once it's a valid one, fills REGISTRY_NAME.
export const roleAssignmentCommand = (loginServer?: string) =>
  [
    `PRINCIPAL_ID=$(az identity show --name ${ACR_IDENTITY_NAME} \\`,
    "  --resource-group RESOURCE_GROUP --query principalId -o tsv)",
    "az role assignment create --role AcrPull \\",
    "  --assignee-object-id $PRINCIPAL_ID --assignee-principal-type ServicePrincipal \\",
    `  --scope $(az acr show --name ${
      loginServer && isValidAcrLoginServer(normalizeAcrLoginServer(loginServer))
        ? acrRegistryName(loginServer)
        : "REGISTRY_NAME"
    } --query id -o tsv)`
  ].join("\n")

export const identityIdsCommand = () =>
  [
    "az account show --query tenantId -o tsv",
    `az identity show --name ${ACR_IDENTITY_NAME} --resource-group RESOURCE_GROUP --query clientId -o tsv`
  ].join("\n")
